import './styles/InvoiceDetail.scss';

import { Grid } from '@mui/material';

import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useJobmarketInvoicesMutation } from 'api/hooks/jobmarket/invoices';
import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import CustomerSearch from './components/CustomerSearch';
import InvoiceSettings from './components/InvoiceSettings';
import Offers from './components/Offers';
import Periods from './components/Periods';
import { invoiceValidationSchema, isStepValid, useInvoiceInitialValues } from './validation';

interface Props {
  onCancel: Function;
  closeHandler: Function;
  companyId: number;
  setDisabledCancel?: Function;
}

export default function InvoiceDetail(props: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { create } = useJobmarketInvoicesMutation();

  const steps = [
    t(translations.pages.invoices.steps.customerSearch()),
    t(translations.pages.invoices.steps.offers()),
    t(translations.pages.invoices.steps.periods()),
    t(translations.pages.invoices.steps.invoiceSettings())
  ];

  const handleSaveClick = async values => {
    if (create.isPending) {
      return;
    }
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }

    const saveBody = {
      companyId: props.companyId,
      offerId: values.selectedOffers[0].id,
      periods: values.selectedPeriods.map(p => p.id),
      tax: values.invoiceSettings.tax,
      customerZPId: !values.customer.useCompanyAsCustomer
        ? values.customer.customer.customerId
        : null
    };

    try {
      await create.mutateAsync(saveBody);
      props.closeHandler();
      dispatch(AlertActions.success(t(translations.pages.invoices.alerts.saveSuccessful())));
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.invoices.alerts.saveFailed())));
    }

    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
  };

  const InvoiceDetailStep = ({ step }) => {
    switch (step) {
      case 0:
        return <CustomerSearch companyId={props.companyId} />;
      case 1:
        return <Offers companyId={props.companyId} />;
      case 2:
        return <Periods />;
      case 3:
        return <InvoiceSettings companyId={props.companyId} />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="invoice-detail-modal">
      <Formik
        isInitialValid={false}
        initialValues={useInvoiceInitialValues()}
        onSubmit={values => {
          handleSaveClick(values);
        }}
        validationSchema={invoiceValidationSchema}
      >
        <MultistepForm
          submitting={create.isPending}
          steps={steps}
          stepValidator={isStepValid}
          onCancel={props.onCancel}
        >
          {activeStep => (
            <Grid container className="invoice-detail-modal__body">
              <InvoiceDetailStep step={activeStep} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
